import "./TermOfServiceModal.css";

import { useEffect, useRef, useState } from "react";

import { Button, ErrorBox, Modal } from "../../../../widget";

interface TermOfServiceModalProps {
  show: boolean;
  productName: string;
  isAcceptAndContinueLoading: boolean;
  errorMessages?: string[];
  onAcceptAndContinue: () => void;
  onClose: () => void;
}

const termsParagraphs: string[] = [
  "Please read these Terms carefully before using any Services made available on our Website. By browsing, accessing and/or using any of the Services available on this Website or opening an Account, you represent that you have full, valid and current authority to represent and act on behalf of the Participant and signify that you have read and accepted these Terms on the Participant's behalf. If you do not agree to these Terms, the Participant and any of its users may not access or otherwise use the Services.",
  "The Participant acknowledges and agrees that Kana Earth does not in any way guarantee or make any representations (including by omission) as to legal title to the Units. The Participant further acknowledges that it is not possible to sell or purchase the Units via the Services. Participants must conduct their own due diligence in respect of any Project and any Units and any reliance on information made available via the Services is at the Participant's own risk.",
  "If you do not agree to these Terms, please exit our Website and refrain from any further use and/or access to our Website and Services.",
];

const termsOfServiceUrl = "https://www.kana.earth/legal/terms-of-service";
const privacyNoticeUrl = "https://www.kana.earth/legal/privacy-policy";

export const TermOfServiceModal = ({
  show,
  productName,
  isAcceptAndContinueLoading,
  errorMessages = [],
  onAcceptAndContinue,
  onClose,
}: TermOfServiceModalProps): JSX.Element => {
  const isSeedProduct = productName === "Seed";
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const termsContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!show) {
      return;
    }

    setHasScrolledToBottom(false);
    setIsAccepted(false);

    if (termsContainerRef.current) {
      termsContainerRef.current.scrollTop = 0;
    }
  }, [show]);

  const handleTermsScroll = (): void => {
    if (hasScrolledToBottom || !termsContainerRef.current) {
      return;
    }

    const hasReachedBottom =
      termsContainerRef.current.scrollTop + termsContainerRef.current.clientHeight >=
      termsContainerRef.current.scrollHeight - 4;

    if (hasReachedBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleCheckboxChange = (): void => {
    if (!hasScrolledToBottom) {
      return;
    }

    setIsAccepted((prevState) => !prevState);
  };

  return (
    <Modal show={show} title="" onClose={onClose} className="TermOfServiceModal">
      <div className="TermOfServiceModalContentContainer">
        <div className="TermOfServiceModalTitleContainer">
          <h2 className={isSeedProduct ? "TermOfServiceModalTitleSeed" : "TermOfServiceModalTitleHub"}>
            Terms of service
          </h2>
          <p>Version 1.3 • Updated: 01 April 2026</p>
        </div>
        <div className="TermOfServiceModalMainContainer">
          <p className="TermOfServiceModalIntro body2">
            Welcome to {productName} by Kana Earth. These terms govern your use of our services. You must agree with the
            terms to continue to the platform.
          </p>
          <div ref={termsContainerRef} className="TermOfServiceModalTermsContainer" onScroll={handleTermsScroll}>
            <div className="TermOfServiceModalTermsText">
              <h3>Terms of Service</h3>
              {termsParagraphs.map((paragraph) => (
                <p key={paragraph} className="body1">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
          <div className="TermOfServiceModalFooterContainer">
            <div className="TermOfServiceModalAcceptanceContainer">
              <p className="body1">
                I understand and accept the{" "}
                <a href={termsOfServiceUrl} target="_blank" rel="noreferrer">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href={privacyNoticeUrl} target="_blank" rel="noreferrer">
                  Privacy Notice
                </a>
                , and have the authority to bind my organisation.
              </p>
              <button
                type="button"
                className={`TermOfServiceModalCheckboxButton ${
                  !hasScrolledToBottom ? "TermOfServiceModalCheckboxButtonDisabled" : ""
                }`}
                role="checkbox"
                aria-checked={isAccepted}
                aria-label="Accept terms of service"
                disabled={!hasScrolledToBottom}
                onClick={handleCheckboxChange}
              >
                <span
                  className={`TermOfServiceModalCheckboxSquare ${isAccepted ? "TermOfServiceModalCheckboxChecked" : ""}`}
                />
              </button>
            </div>
            <Button
              text="Accept and continue"
              className="TermOfServiceModalPrimaryButton"
              onClick={onAcceptAndContinue}
              isLoading={isAcceptAndContinueLoading}
              disabled={!hasScrolledToBottom || !isAccepted}
            />
          </div>
        </div>
        {errorMessages.length > 0 && <ErrorBox text={errorMessages} />}
        <a href={termsOfServiceUrl} target="_blank" rel="noreferrer" className="TermOfServiceModalDownloadLink body1">
          Download a copy
        </a>
      </div>
    </Modal>
  );
};
